import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { GroupEmailModal } from "@/components/GroupsSection";
import MapPanel from "@/components/MapPanel";
import { Network, Plus, Pencil, Trash2, X, Users, CalendarHeart, Star, MapPin, Search, UserPlus, Mail, Send, LayoutGrid, List as ListIcon, ArrowRight } from "lucide-react";

const fmt = (s) => { if (!s) return "TBD"; const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); };
const empty = { name: "", type: "chapter", description: "", location: "", states: [] };

export default function GroupsAdmin() {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(null);
  const [manage, setManage] = useState(null);
  const [members, setMembers] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("ud_groups_view") || "grid");
  const setViewMode = (v) => { setView(v); localStorage.setItem("ud_groups_view", v); };
  const load = () => api.get("/groups").then((r) => setGroups(r.data.groups)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const loadMembers = () => api.get("/groups/members-map").then((r) => setMembers(r.data.members || [])).catch(() => {});
  useEffect(() => { load(); loadMembers(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (form.id) await api.put(`/groups/${form.id}`, form);
      else await api.post("/groups", form);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (g) => { if (!window.confirm(`Delete "${g.name}"?`)) return; await api.delete(`/groups/${g.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="fade-up">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Community</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Chapters &amp; Groups</h1>
          <p className="text-sm text-muted-foreground mt-1">Geographic chapters and interest clubs with their own rosters and events.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-border overflow-hidden">
            <button data-testid="groups-view-grid" onClick={() => setViewMode("grid")} title="Grid view" className={`p-2 ${view === "grid" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button data-testid="groups-view-list" onClick={() => setViewMode("list")} title="List view" className={`p-2 border-l border-border ${view === "list" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><ListIcon className="h-4 w-4" /></button>
          </div>
          <button data-testid="new-group-btn" onClick={() => setForm({ ...empty })} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90"><Plus className="h-4 w-4" /> New Group</button>
        </div>
      </div>

      <div className="mb-6">
        <MapPanel alumni={members} title="Chapter Map" />
      </div>

      {groups.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Network className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <div className="font-serif text-xl">No groups yet</div>
          <p className="text-sm text-muted-foreground mt-2">Create your first chapter or club to get started.</p>
        </div>
      ) : view === "grid" ? (
        <div className="grid md:grid-cols-3 gap-4">
          {groups.map((g) => (
            <div key={g.id} data-testid={`admin-group-${g.id}`} className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-start justify-between">
                <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${g.type === "chapter" ? "bg-accent/20 text-primary" : "bg-secondary/10 text-secondary"}`}>{g.type}</span>
                <div className="flex gap-1">
                  <button data-testid={`group-edit-${g.id}`} onClick={() => setForm({ id: g.id, name: g.name, type: g.type, description: g.description, location: g.location, states: g.states || [] })} className="p-1.5 rounded hover:bg-muted text-muted-foreground"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`group-delete-${g.id}`} onClick={() => del(g)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="font-serif text-lg tracking-tight mt-2">{g.name}</div>
              {g.type === "chapter" && (g.states?.length > 0 || g.location) && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><MapPin className="h-3 w-3" /> {g.states?.length > 0 ? g.states.join(", ") : g.location}</div>
              )}
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-3">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {g.member_count} members</span>
                <span className="flex items-center gap-1"><CalendarHeart className="h-3.5 w-3.5" /> {g.event_count} events</span>
              </div>
              <button data-testid={`group-manage-${g.id}`} onClick={() => setManage(g.id)} className="mt-4 w-full py-2 rounded-md border border-border text-sm hover:bg-muted">Manage roster &amp; events</button>
            </div>
          ))}
        </div>
      ) : (
        <div data-testid="groups-list" className="bg-card border border-border rounded-lg shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono">
              <tr>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">States</th>
                <th className="text-right px-4 py-2.5">Members</th>
                <th className="text-right px-4 py-2.5">Events</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {groups.map((g) => (
                <tr key={g.id} data-testid={`group-row-${g.id}`} className="hover:bg-muted/40">
                  <td className="px-4 py-2.5"><span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${g.type === "chapter" ? "bg-accent/20 text-primary" : "bg-secondary/10 text-secondary"}`}>{g.type}</span></td>
                  <td className="px-4 py-2.5 font-medium">{g.name}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{g.type === "chapter" ? (g.states?.length ? g.states.join(", ") : g.location || "—") : "—"}</td>
                  <td className="px-4 py-2.5 text-right">{g.member_count}</td>
                  <td className="px-4 py-2.5 text-right text-muted-foreground">{g.event_count}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`group-manage-row-${g.id}`} onClick={() => setManage(g.id)} className="text-xs px-2.5 py-1 rounded-md border border-border hover:bg-muted mr-1">Manage</button>
                    <button data-testid={`group-edit-row-${g.id}`} onClick={() => setForm({ id: g.id, name: g.name, type: g.type, description: g.description, location: g.location, states: g.states || [] })} className="p-1.5 rounded hover:bg-muted text-muted-foreground inline-flex align-middle"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`group-delete-row-${g.id}`} onClick={() => del(g)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive inline-flex align-middle"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && <FormModal form={form} setForm={setForm} onSave={save} onClose={() => setForm(null)} />}
      {manage && <ManageModal id={manage} onClose={() => { setManage(null); load(); loadMembers(); }} />}
    </div>
  );
}

function FormModal({ form, setForm, onSave, onClose }) {
  return createPortal((
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-md rounded-lg shadow-2xl my-10" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border"><span className="font-serif text-lg">{form.id ? "Edit Group" : "New Group"}</span><button onClick={onClose}><X className="h-5 w-5 text-muted-foreground" /></button></div>
        <div className="p-5 space-y-4">
          <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Name</span>
            <input data-testid="group-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ud-input mt-1" placeholder="Dallas Chapter" /></label>
          <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Type</span>
            <select data-testid="group-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ud-input mt-1"><option value="chapter">Chapter (geographic)</option><option value="club">Club / Committee (interest)</option></select></label>
          {form.type === "chapter" && (
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">States / Territories <span className="normal-case tracking-normal">(select one or more)</span></span>
              <StatePicker value={form.states || []} onChange={(states) => setForm({ ...form, states })} />
            </div>
          )}
          <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Description</span>
            <textarea data-testid="group-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="ud-input mt-1 font-sans" /></label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="group-save" onClick={onSave} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">Save</button>
        </div>
      </div>
    </div>
  ), document.body);
}

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"],
  ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"],
  ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"],
  ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"],
  ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"],
];
const US_TERRITORIES = [
  ["PR", "Puerto Rico"], ["GU", "Guam"], ["VI", "U.S. Virgin Islands"], ["AS", "American Samoa"], ["MP", "N. Mariana Islands"],
];

function StatePicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const toggle = (ab) => onChange(value.includes(ab) ? value.filter((x) => x !== ab) : [...value, ab]);
  const match = ([ab, name]) => (name + " " + ab).toLowerCase().includes(q.toLowerCase());
  const section = (title, list) => {
    const items = list.filter(match);
    if (!items.length) return null;
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-2 mb-1">{title}</div>
        <div className="flex flex-wrap gap-1.5">
          {items.map(([ab, name]) => (
            <button key={ab} type="button" data-testid={`state-chip-${ab}`} onClick={() => toggle(ab)} title={name}
              className={`text-xs px-2 py-1 rounded-md border transition-colors ${value.includes(ab) ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>{ab}</button>
          ))}
        </div>
      </div>
    );
  };
  return (
    <div className="mt-1 border border-border rounded-md p-3">
      <input data-testid="state-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter states…" className="ud-input text-sm mb-1" />
      {value.length > 0 && (
        <div className="text-xs text-muted-foreground mb-1" data-testid="state-picker-summary">{value.length} selected: <span className="text-foreground font-medium">{value.join(", ")}</span></div>
      )}
      <div className="max-h-52 overflow-y-auto pr-1">
        {section("States", US_STATES)}
        {section("Territories", US_TERRITORIES)}
        {US_STATES.filter(match).length + US_TERRITORIES.filter(match).length === 0 && <div className="text-xs text-muted-foreground py-2">No match.</div>}
      </div>
    </div>
  );
}

function ManageModal({ id, onClose }) {
  const [g, setG] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [ev, setEv] = useState({ title: "", date: "", location: "", description: "" });
  const [emailing, setEmailing] = useState(false);
  const load = () => api.get(`/groups/${id}`).then((r) => setG(r.data));
  useEffect(() => { load(); }, [id]);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    const { data } = await api.get(`/alumni?q=${encodeURIComponent(val)}`);
    setResults((data.alumni || data).slice(0, 8));
  };
  const member = async (alumnus_id, action) => { await api.post(`/groups/${id}/members`, { alumnus_id, action }); load(); };
  const leader = async (alumnus_id, action) => { try { await api.post(`/groups/${id}/leaders`, { alumnus_id, action }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const addEvent = async () => { if (!ev.title.trim()) { toast.error("Event title required"); return; } await api.post(`/groups/${id}/events`, ev); setEv({ title: "", date: "", location: "", description: "" }); load(); };
  const delEvent = async (eid) => { await api.delete(`/groups/${id}/events/${eid}`); load(); };

  return createPortal((
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-2xl rounded-lg shadow-2xl my-8" onMouseDown={(e) => e.stopPropagation()} data-testid="group-manage-modal">
        {!g ? <div className="p-8 text-muted-foreground">Loading…</div> : (
          <>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border"><span className="font-serif text-lg">{g.name} · Manage</span><div className="flex items-center gap-2"><button data-testid="group-email-btn" onClick={() => setEmailing(true)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-secondary text-white hover:brightness-110"><Mail className="h-4 w-4" /> Email group</button><button data-testid="manage-close" onClick={onClose}><X className="h-5 w-5 text-muted-foreground" /></button></div></div>
            <div className="p-5 grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Members ({g.members.length})</div>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input data-testid="member-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Add member by name…" className="ud-input pl-8 text-sm" />
                </div>
                {results.length > 0 && (
                  <div className="border border-border rounded-md mb-3 divide-y divide-border">
                    {results.map((a) => (
                      <button key={a.id} data-testid={`add-member-${a.id}`} onClick={() => { member(a.id, "add"); setQ(""); setResults([]); }} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"><UserPlus className="h-3.5 w-3.5 text-primary" /> {a.name} {a.class_year ? `’${String(a.class_year).slice(-2)}` : ""}</button>
                    ))}
                  </div>
                )}
                <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                  {g.members.map((m) => (
                    <li key={m.id} className="flex items-center gap-2 text-sm border border-border rounded-md px-2.5 py-1.5">
                      {m.is_leader && <Star className="h-3.5 w-3.5 text-accent shrink-0" />}
                      <span className="flex-1 truncate">{m.name}{m.class_year ? ` ’${String(m.class_year).slice(-2)}` : ""}</span>
                      <button data-testid={`toggle-leader-${m.id}`} onClick={() => leader(m.id, m.is_leader ? "remove" : "add")} className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${m.is_leader ? "bg-accent/20 text-accent" : "border border-border text-muted-foreground hover:bg-muted"}`}>{m.is_leader ? "Leader" : "Make leader"}</button>
                      <button data-testid={`remove-member-${m.id}`} onClick={() => member(m.id, "remove")} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                  {g.members.length === 0 && <li className="text-sm text-muted-foreground">No members yet.</li>}
                </ul>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><CalendarHeart className="h-3.5 w-3.5" /> Events ({g.events.length})</div>
                <div className="space-y-2 mb-3">
                  <input data-testid="event-title" value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} placeholder="Event title" className="ud-input text-sm" />
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="event-date" type="date" value={ev.date} onChange={(e) => setEv({ ...ev, date: e.target.value })} className="ud-input text-sm" />
                    <input data-testid="event-location" value={ev.location} onChange={(e) => setEv({ ...ev, location: e.target.value })} placeholder="Location" className="ud-input text-sm" />
                  </div>
                  <button data-testid="add-event" onClick={addEvent} className="w-full py-2 rounded-md bg-secondary text-white text-sm font-medium hover:brightness-110 flex items-center justify-center gap-1.5"><Plus className="h-4 w-4" /> Add event</button>
                </div>
                <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                  {g.events.map((e) => (
                    <li key={e.id} className="flex items-start gap-2 text-sm border border-border rounded-md px-2.5 py-1.5">
                      <div className="flex-1"><div className="font-medium">{e.title}</div><div className="text-xs text-muted-foreground">{fmt(e.date)}{e.location ? ` · ${e.location}` : ""}</div></div>
                      <button data-testid={`del-event-${e.id}`} onClick={() => delEvent(e.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                  {g.events.length === 0 && <li className="text-sm text-muted-foreground">No events yet.</li>}
                </ul>
              </div>
            </div>
          </>
        )}
        {emailing && g && <GroupEmailModal id={id} name={g.name} onClose={() => setEmailing(false)} />}
      </div>
    </div>
  ), document.body);
}
